// ═══════════════════════════════════════════════════════════════
// DEBRIS ADMS — Truck Certification Registry
// Searchable truck certification archive for FEMA audit readiness.
// Bulk import from PDF filenames, capacity validation against tickets.
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select,
  Modal, Stack, SimpleGrid, Checkbox, ActionIcon,
} from '../../components/gab/GabComponents';
import {
  IconSearch, IconTruck, IconUpload, IconAlertTriangle,
  IconCertificate, IconRefresh, IconEye, IconX,
  IconFileText, IconCheck, IconClock, IconFilter, IconFileTypePdf, IconDownload,
  IconPencil, IconBuilding,
} from '@tabler/icons-react';
import { exportCSV } from '../../lib/exportCSV';
import { notifications } from '../../lib/notifications';
import { getV2FileDownloadUrl } from '../../lib/uploadFile';
import { useDebrisStore } from '../../store/debris-store';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { useNavigate } from 'react-router-dom';

// ── Status colors ──

const STATUS_COLORS: Record<string, string> = {
  certified: 'var(--color-success)',
  expired: 'var(--color-error)',
  pending: '#C68700',
};

const STATUS_LABELS: Record<string, string> = {
  certified: 'Certified',
  expired: 'Expired',
  pending: 'Pending',
};

// ── TRMETA decode ──

const TRMETA_RE = /\|\|\|TRMETA\|\|\|([\s\S]*?)\|\|\|ENDTRMETA\|\|\|/;
function decodeTrMeta(notes: string): Record<string, string> {
  if (!notes) return {};
  const m = notes.match(TRMETA_RE);
  if (!m) return {};
  try { return JSON.parse(m[1]); } catch { return {}; }
}

// ── Helpers ──

function formatDate(iso: string): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
}

function isExpired(expiresAt: string): boolean {
  if (!expiresAt) return false;
  return new Date(expiresAt) < new Date();
}

function computeStatus(truck: { certStatus: string; certExpiresAt: string }): string {
  if (truck.certStatus === 'certified' && isExpired(truck.certExpiresAt)) return 'expired';
  return truck.certStatus || 'pending';
}

// ── Capacity alert type ──

interface CapacityAlert {
  ticketNumber: string;
  truckNumber: string;
  ticketCY: number;
  truckCapacity: number;
  overloadPct: number;
}

// ════════════════════════════════════════════════════════════════
// Main component
// ════════════════════════════════════════════════════════════════

export default function TruckRegistry() {
  const { canEdit } = useModulePermission('debris');
  const navigate = useNavigate();

  // Store
  const {
    trucks, fetchTrucks, loadingTrucks, createTruck, updateTruck,
    contractors, fetchContractors,
    tickets, fetchTickets,
  } = useDebrisStore();

  // Local state
  const [search, setSearch] = useState('');
  const [contractorFilter, setContractorFilter] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [selectedTruck, setSelectedTruck] = useState<any | null>(null);

  // Assign contractor state
  const [assigningId, setAssigningId] = useState<number | null>(null);
  const [assignContractorValue, setAssignContractorValue] = useState<string | null>(null);
  const [assignSaving, setAssignSaving] = useState(false);
  const [modalEditingContractor, setModalEditingContractor] = useState(false);

  // Import state
  const [showImport, setShowImport] = useState(false);
  const [importFiles, setImportFiles] = useState<File[]>([]);
  const [importContractor, setImportContractor] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState<{ success: number; failed: number; errors: string[] } | null>(null);
  const [capacityAlerts, setCapacityAlerts] = useState<CapacityAlert[]>([]);

  const [pdfLoading, setPdfLoading] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Inline PDF viewer ──
  const [pdfViewerUrl, setPdfViewerUrl] = useState<string | null>(null);
  const [pdfViewerTitle, setPdfViewerTitle] = useState('');

  const viewPdf = async (documentId: string, title?: string) => {
    if (!documentId) return;
    setPdfLoading(documentId);
    try {
      const rawUrl = await getV2FileDownloadUrl(documentId);
      const proxyUrl = `/api/filepreview?url=${encodeURIComponent(rawUrl)}`;
      setPdfViewerUrl(proxyUrl);
      setPdfViewerTitle(title || 'Document Viewer');
    } catch (e) {
      console.error('Failed to get download URL:', e);
      notifications.show({ message: 'Could not load document', color: 'var(--color-error)' });
    } finally {
      setPdfLoading(null);
    }
  };

  // Load data
  useEffect(() => {
    fetchTrucks();
    fetchContractors();
    fetchTickets();
  }, []);

  // Contractor options for filter
  const contractorOptions = useMemo(() => [
    { value: '', label: 'All Contractors' },
    ...contractors.map(c => ({ value: String(c.id), label: c.companyName })),
  ], [contractors]);

  // Filter + sort
  const filtered = useMemo(() => {
    let result = trucks.map(t => ({ ...t, _status: computeStatus(t) }));
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(t =>
        t.truckNumber.toLowerCase().includes(q) ||
        t.placardNumber.toLowerCase().includes(q) ||
        t.licensePlate.toLowerCase().includes(q)
      );
    }
    if (contractorFilter) {
      result = result.filter(t => String(t.contractorId) === contractorFilter);
    }
    return result;
  }, [trucks, search, contractorFilter]);

  const sorted = useMemo(() => {
    if (!sortField) return filtered;
    return [...filtered].sort((a, b) => {
      const va = (a as any)[sortField] ?? '';
      const vb = (b as any)[sortField] ?? '';
      const n = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
      return sortDir === 'asc' ? n : -n;
    });
  }, [filtered, sortField, sortDir]);

  // Stats
  const stats = useMemo(() => {
    const all = trucks.map(t => computeStatus(t));
    return {
      total: trucks.length,
      certified: all.filter(s => s === 'certified').length,
      expired: all.filter(s => s === 'expired').length,
      pending: all.filter(s => s === 'pending').length,
    };
  }, [trucks]);

  // Sort toggle
  const toggleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const sortIcon = (col: string) =>
    sortField === col ? (sortDir === 'desc' ? '↓' : '↑') : <span style={{ opacity: 0.25 }}>↑</span>;

  const thStyle = (col: string): React.CSSProperties => ({
    cursor: 'pointer', userSelect: 'none',
    color: sortField === col ? '#4B3FFF' : '#64748b',
    padding: '10px 12px', textAlign: 'left', fontSize: 13, fontWeight: 600,
    whiteSpace: 'nowrap',
  });

  // Get contractor name
  const getContractorName = (id: string) =>
    contractors.find(c => String(c.id) === id)?.companyName || '—';

  const contractorSelectOptions = useMemo(() =>
    contractors.map(c => ({ value: String(c.id), label: c.companyName || `#${c.id}` })),
    [contractors],
  );

  const handleAssignContractor = async (truckId: number) => {
    if (!assignContractorValue) return;
    setAssignSaving(true);
    try {
      await updateTruck(truckId, { contractorId: assignContractorValue });
      notifications.show({ message: 'Contractor assigned', color: 'var(--color-success)' });
      setAssigningId(null);
      setModalEditingContractor(false);
      setAssignContractorValue(null);
      // Update selectedTruck if it's open
      if (selectedTruck?.id === truckId) {
        const updated = useDebrisStore.getState().trucks.find(t => t.id === truckId);
        if (updated) setSelectedTruck(updated);
      }
    } catch {
      notifications.show({ message: 'Failed to assign contractor', color: 'var(--color-error)' });
    } finally {
      setAssignSaving(false);
    }
  };

  // ── Import logic ──

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setImportFiles(files);
    setImportResults(null);
    setCapacityAlerts([]);
  };

  const parseFilename = (name: string): { truckNumber: string; capacity: number } | null => {
    // Pattern: TampaAshbrittMilton_{TruckNo}_{CapacityCY}.pdf
    const base = name.replace(/\.pdf$/i, '');
    const parts = base.split('_');
    if (parts.length >= 3) {
      const truckNumber = parts[parts.length - 2];
      const capacity = parseFloat(parts[parts.length - 1]);
      if (truckNumber && !isNaN(capacity)) {
        return { truckNumber, capacity };
      }
    }
    return null;
  };

  const runImport = async () => {
    if (!importContractor) {
      notifications.show({ title: 'Contractor required', message: 'Select a contractor for imported trucks', color: 'red' });
      return;
    }
    if (importFiles.length === 0) {
      notifications.show({ title: 'No files', message: 'Select PDF files to import', color: 'red' });
      return;
    }

    setImporting(true);
    let success = 0;
    let failed = 0;
    const errors: string[] = [];
    const today = new Date().toISOString().slice(0, 10);
    // Default expiration: 1 year from today
    const expiresDate = new Date();
    expiresDate.setFullYear(expiresDate.getFullYear() + 1);
    const expiresAt = expiresDate.toISOString().slice(0, 10);

    for (const file of importFiles) {
      const parsed = parseFilename(file.name);
      if (!parsed) {
        errors.push(`${file.name}: could not parse truck # / capacity from filename`);
        failed++;
        continue;
      }
      try {
        await createTruck({
          truckNumber: parsed.truckNumber,
          cubicYardCapacity: parsed.capacity,
          contractorId: importContractor,
          certStatus: 'certified',
          certifiedDate: today,
          certExpiresAt: expiresAt,
          notes: `Imported from ${file.name}`,
        });
        success++;
      } catch (e) {
        errors.push(`${file.name}: failed to create record`);
        failed++;
      }
    }

    setImportResults({ success, failed, errors });
    setImporting(false);

    if (success > 0) {
      notifications.show({ title: 'Import complete', message: `${success} truck(s) imported`, color: 'green' });
      // Run capacity validation after import
      runCapacityValidation();
    }
  };

  // ── Capacity validation ──

  const runCapacityValidation = useCallback(() => {
    const alerts: CapacityAlert[] = [];
    const truckMap = new Map<string, { capacity: number; truckNumber: string }>();
    trucks.forEach(t => {
      if (t.truckNumber) {
        truckMap.set(t.truckNumber.toLowerCase(), {
          capacity: parseFloat(t.cubicYardCapacity) || 0,
          truckNumber: t.truckNumber,
        });
      }
    });

    tickets.forEach(ticket => {
      const cy = parseFloat(ticket.calculatedCubicYards) || 0;
      if (cy <= 0) return;
      // Match by truckId or truckNumber
      const truckNum = ticket.truckNumber?.toLowerCase();
      const matched = truckNum ? truckMap.get(truckNum) : undefined;
      if (matched && matched.capacity > 0 && cy > matched.capacity) {
        alerts.push({
          ticketNumber: ticket.ticketNumber || String(ticket.id),
          truckNumber: matched.truckNumber,
          ticketCY: cy,
          truckCapacity: matched.capacity,
          overloadPct: Math.round(((cy - matched.capacity) / matched.capacity) * 100),
        });
      }
    });

    setCapacityAlerts(alerts);
  }, [trucks, tickets]);

  // ════════════════════════════════════════════════════════════════
  // Render
  // ════════════════════════════════════════════════════════════════

  return (
    <Stack gap={12}>
      <Group style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <Title order={3}>Truck Certification Registry</Title>
        <Group gap={8}>
          <Button variant="light" leftSection={<IconRefresh size={16} />} onClick={() => { fetchTrucks(); fetchTickets(); }}>
            Refresh
          </Button>
          <Button variant="light" leftSection={<IconDownload size={16} />}
            onClick={() => {
              const headers = ['Truck Number', 'Contractor', 'Capacity CY', 'License Plate', 'Cert Status', 'Driver Name'];
              const rows = sorted.map(t => [
                t.truckNumber || '',
                getContractorName(t.contractorId),
                t.cubicYardCapacity || '',
                t.licensePlate || '',
                t._status || '',
                t.driverName || '',
              ]);
              exportCSV('trucks.csv', headers, rows);
            }}>
            Export CSV
          </Button>
          {canEdit && (
            <Button leftSection={<IconUpload size={16} />} onClick={() => setShowImport(true)}>
              Import Certifications
            </Button>
          )}
        </Group>
      </Group>

      {/* Search + Filter */}
      <Group gap={8}>
        <TextInput
          placeholder="Search by truck #, placard, or plate..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e: any) => setSearch(e.target.value)}
          style={{ flex: 1, maxWidth: 400 }}
        />
        <Select
          data={contractorOptions}
          value={contractorFilter}
          onChange={(v: string | null) => setContractorFilter(v || '')}
          placeholder="Filter by contractor"
          style={{ width: 240 }}
        />
        {(search || contractorFilter) && (
          <Button variant="subtle" size="xs" onClick={() => { setSearch(''); setContractorFilter(''); }}>
            Clear
          </Button>
        )}
      </Group>

      {/* Stats row */}
      <SimpleGrid cols={4} spacing="sm">
        {[
          { label: 'Total Trucks', value: stats.total, color: '#4B3FFF', icon: <IconTruck size={20} /> },
          { label: 'Certified', value: stats.certified, color: 'var(--color-success)', icon: <IconCheck size={20} /> },
          { label: 'Expired', value: stats.expired, color: 'var(--color-error)', icon: <IconAlertTriangle size={20} /> },
          { label: 'Pending', value: stats.pending, color: '#C68700', icon: <IconClock size={20} /> },
        ].map(s => (
          <Paper key={s.label} withBorder p="md" style={{ borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div style={{ color: s.color, marginBottom: 4 }}>{s.icon}</div>
            <div style={{ fontSize: 32, fontWeight: 800, color: s.color }}>{s.value}</div>
            <Text size="sm" c="dimmed">{s.label}</Text>
          </Paper>
        ))}
      </SimpleGrid>

      {/* Table */}
      <Paper withBorder p={0} style={{ borderRadius: 8 }}>
        <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 420px)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc' }}>
              <tr>
                {[
                  { key: 'truckNumber', label: 'Truck #' },
                  { key: 'cubicYardCapacity', label: 'Capacity (CY)' },
                  { key: 'contractorId', label: 'Contractor' },
                  { key: 'licensePlate', label: 'License Plate' },
                  { key: 'certifiedDate', label: 'Cert Date' },
                  { key: 'certExpiresAt', label: 'Cert Expires' },
                  { key: '_status', label: 'Status' },
                ].map(col => (
                  <th key={col.key} onClick={() => toggleSort(col.key)} style={thStyle(col.key)}>
                    {col.label} {sortIcon(col.key)}
                  </th>
                ))}
                <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: 13, fontWeight: 600, color: '#64748b' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 && (
                <tr><td colSpan={8} style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>
                  {loadingTrucks ? 'Loading trucks...' : 'No trucks found'}
                </td></tr>
              )}
              {sorted.map(t => {
                const status = t._status;
                return (
                  <tr
                    key={t.id}
                    onClick={() => setSelectedTruck(t)}
                    style={{ cursor: 'pointer', borderBottom: '1px solid #f1f5f9' }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                    onMouseLeave={e => (e.currentTarget.style.background = '')}
                  >
                    <td style={{ padding: '10px 12px', fontWeight: 600 }}>{t.truckNumber || '—'}</td>
                    <td style={{ padding: '10px 12px' }}>
                      {t.cubicYardCapacity} CY
                      {t.hasTailgate === 'true' && (
                        <span style={{ fontSize: 11, color: '#C68700', marginLeft: 6 }} title="Tailgate: 15% FEMA reduction">
                          (TG)
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      {assigningId === t.id ? (
                        <Group gap={4} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                          <Select
                            data={contractorSelectOptions}
                            value={assignContractorValue}
                            onChange={(v: string | null) => setAssignContractorValue(v)}
                            placeholder="Select contractor..."
                            style={{ width: 180 }}
                          />
                          <Button size="xs" loading={assignSaving} disabled={!assignContractorValue}
                            onClick={(e: React.MouseEvent) => { e.stopPropagation(); handleAssignContractor(t.id!); }}>
                            Save
                          </Button>
                          <Button size="xs" variant="subtle"
                            onClick={(e: React.MouseEvent) => { e.stopPropagation(); setAssigningId(null); setAssignContractorValue(null); }}>
                            Cancel
                          </Button>
                        </Group>
                      ) : (
                        <Group gap={6}>
                          <span>{t.contractorId ? getContractorName(t.contractorId) : <span style={{ color: '#94a3b8' }}>—</span>}</span>
                          {canEdit && !t.contractorId && (
                            <Button size="xs" variant="subtle"
                              leftSection={<IconBuilding size={12} />}
                              onClick={(e: React.MouseEvent) => { e.stopPropagation(); setAssigningId(t.id!); setAssignContractorValue(null); }}
                              style={{ padding: '2px 8px', fontSize: 11 }}>
                              Assign
                            </Button>
                          )}
                        </Group>
                      )}
                    </td>
                    <td style={{ padding: '10px 12px' }}>{t.licensePlate || '—'}</td>
                    <td style={{ padding: '10px 12px' }}>{formatDate(t.certifiedDate)}</td>
                    <td style={{ padding: '10px 12px' }}>{formatDate(t.certExpiresAt)}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <Badge
                        size="sm"
                        style={{
                          backgroundColor: `${STATUS_COLORS[status] || '#94a3b8'}18`,
                          color: STATUS_COLORS[status] || '#94a3b8',
                          border: `1px solid ${STATUS_COLORS[status] || '#94a3b8'}40`,
                        }}
                      >
                        {STATUS_LABELS[status] || status}
                      </Badge>
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                      <Group gap={4} style={{ justifyContent: 'flex-end' }}>
                        {(() => {
                          const trMeta = decodeTrMeta(t.notes);
                          const certDocId = trMeta.documentId;
                          if (!certDocId) return null;
                          return (
                            <Button
                              variant="subtle"
                              size="xs"
                              loading={pdfLoading === certDocId}
                              onClick={(e: React.MouseEvent) => { e.stopPropagation(); viewPdf(certDocId); }}
                              title="View Cert PDF"
                              style={{ padding: '4px 8px' }}
                            >
                              <IconFileTypePdf size={16} />
                            </Button>
                          );
                        })()}
                        <Button
                          variant="subtle"
                          size="xs"
                          leftSection={<IconEye size={14} />}
                          onClick={(e: React.MouseEvent) => { e.stopPropagation(); setSelectedTruck(t); }}
                        >
                          View
                        </Button>
                      </Group>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Paper>

      {/* Capacity Alerts */}
      {capacityAlerts.length > 0 && (
        <Paper withBorder p="md" style={{ borderRadius: 8, borderColor: 'var(--color-error)' }}>
          <Group gap={8} mb={8}>
            <IconAlertTriangle size={20} color="var(--color-error)" />
            <Title order={5} style={{ color: 'var(--color-error)' }}>
              Capacity Alerts ({capacityAlerts.length})
            </Title>
          </Group>
          <Text size="sm" c="dimmed" mb={8}>
            Tickets where calculated cubic yards exceed the truck's certified capacity — potential overload or billing fraud.
          </Text>
          <div style={{ overflowX: 'auto', maxHeight: 300, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 1 }}>
                <tr>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#64748b' }}>Ticket #</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#64748b' }}>Truck #</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, color: '#64748b' }}>Ticket CY</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, color: '#64748b' }}>Truck Cap</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, color: '#64748b' }}>Overload</th>
                </tr>
              </thead>
              <tbody>
                {capacityAlerts.map((a, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '8px 10px', fontWeight: 600 }}>{a.ticketNumber}</td>
                    <td style={{ padding: '8px 10px' }}>{a.truckNumber}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', color: 'var(--color-error)', fontWeight: 600 }}>{a.ticketCY.toFixed(1)}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right' }}>{a.truckCapacity.toFixed(1)}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', color: 'var(--color-error)', fontWeight: 600 }}>+{a.overloadPct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Paper>
      )}

      {capacityAlerts.length === 0 && trucks.length > 0 && tickets.length > 0 && (
        <Group gap={4}>
          <Button variant="subtle" size="xs" leftSection={<IconAlertTriangle size={14} />} onClick={runCapacityValidation}>
            Run Capacity Validation
          </Button>
        </Group>
      )}

      {/* ── Truck Detail Modal ── */}
      <Modal opened={!!selectedTruck} onClose={() => { setSelectedTruck(null); setModalEditingContractor(false); setAssignContractorValue(null); }} title="Truck Detail" size="lg">
        {selectedTruck && (() => {
          const _trMeta = decodeTrMeta(selectedTruck.notes);
          return (
          <Stack gap={12}>
            <SimpleGrid cols={2} spacing="sm">
              <div>
                <Text size="xs" c="dimmed" fw={600}>Truck #</Text>
                <Text fw={700} size="lg">{selectedTruck.truckNumber || '—'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" fw={600}>Capacity</Text>
                <Text fw={700} size="lg">{selectedTruck.cubicYardCapacity} CY</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" fw={600}>Contractor</Text>
                {modalEditingContractor ? (
                  <Group gap={6} mt={4}>
                    <Select
                      data={contractorSelectOptions}
                      value={assignContractorValue ?? (selectedTruck.contractorId ? String(selectedTruck.contractorId) : null)}
                      onChange={(v: string | null) => setAssignContractorValue(v)}
                      placeholder="Select contractor..."
                      style={{ flex: 1 }}
                    />
                    <Button size="xs" loading={assignSaving} disabled={!assignContractorValue}
                      onClick={() => handleAssignContractor(selectedTruck.id!)}>
                      Save
                    </Button>
                    <Button size="xs" variant="subtle"
                      onClick={() => { setModalEditingContractor(false); setAssignContractorValue(null); }}>
                      Cancel
                    </Button>
                  </Group>
                ) : (
                  <Group gap={6}>
                    <Text>{selectedTruck.contractorId ? getContractorName(selectedTruck.contractorId) : <span style={{ color: '#94a3b8' }}>Unassigned</span>}</Text>
                    {canEdit && (
                      <ActionIcon size="xs" variant="subtle"
                        onClick={() => { setModalEditingContractor(true); setAssignContractorValue(null); }}
                        title="Change contractor">
                        <IconPencil size={13} />
                      </ActionIcon>
                    )}
                  </Group>
                )}
              </div>
              <div>
                <Text size="xs" c="dimmed" fw={600}>Subcontractor</Text>
                <Text>{String(_trMeta.subcontractor || '—')}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" fw={600}>License Plate</Text>
                <Text>{selectedTruck.licensePlate || '—'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" fw={600}>Tag State</Text>
                <Text>{String(_trMeta.tagState || '—')}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" fw={600}>Vehicle Type</Text>
                <Text>{String(_trMeta.vehicleType || selectedTruck.truckType || '—')}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" fw={600}>Tag Expiration</Text>
                <Text>{String(_trMeta.tagExpiration || '—')}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" fw={600}>Driver</Text>
                <Text>{selectedTruck.driverName || '—'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" fw={600}>Driver Phone</Text>
                <Text>{selectedTruck.driverPhone || '—'}</Text>
              </div>
              {_trMeta.tareWeight && <div>
                <Text size="xs" c="dimmed" fw={600}>Tare Weight</Text>
                <Text>{String(_trMeta.tareWeight)}</Text>
              </div>}
              {_trMeta.certifiedByName && <div>
                <Text size="xs" c="dimmed" fw={600}>Certified By</Text>
                <Text>{String(_trMeta.certifiedByName)}</Text>
              </div>}
            </SimpleGrid>

            <div style={{ borderTop: '1px solid #e8eaf0', paddingTop: 12 }}>
              <Text size="sm" fw={600} mb={8}>Certification</Text>
              <SimpleGrid cols={3} spacing="sm">
                <div>
                  <Text size="xs" c="dimmed" fw={600}>Status</Text>
                  <Badge
                    size="lg"
                    style={{
                      backgroundColor: `${STATUS_COLORS[computeStatus(selectedTruck)] || '#94a3b8'}18`,
                      color: STATUS_COLORS[computeStatus(selectedTruck)] || '#94a3b8',
                      border: `1px solid ${STATUS_COLORS[computeStatus(selectedTruck)] || '#94a3b8'}40`,
                    }}
                  >
                    {STATUS_LABELS[computeStatus(selectedTruck)] || selectedTruck.certStatus}
                  </Badge>
                </div>
                <div>
                  <Text size="xs" c="dimmed" fw={600}>Cert Date</Text>
                  <Text>{formatDate(selectedTruck.certifiedDate)}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed" fw={600}>Expires</Text>
                  <Text style={{ color: isExpired(selectedTruck.certExpiresAt) ? 'var(--color-error)' : undefined }}>
                    {formatDate(selectedTruck.certExpiresAt)}
                  </Text>
                </div>
              </SimpleGrid>
            </div>

            {/* View Cert PDF */}
            {(() => {
              const certDocId = _trMeta.documentId;
              if (!certDocId) return null;
              return (
                <div style={{ borderTop: '1px solid #e8eaf0', paddingTop: 12 }}>
                  <Group gap={8}>
                    <Button
                      size="xs"
                      variant="light"
                      leftSection={<IconFileTypePdf size={14} />}
                      loading={pdfLoading === certDocId}
                      onClick={(e: React.MouseEvent) => { e.stopPropagation(); viewPdf(certDocId); }}
                    >
                      View Cert PDF
                    </Button>
                    {_trMeta.certFileName && (
                      <Text size="xs" c="dimmed">{_trMeta.certFileName}</Text>
                    )}
                  </Group>
                </div>
              );
            })()}

            {/* Tailgate indicator */}
            <div style={{ borderTop: '1px solid #e8eaf0', paddingTop: 12 }}>
              <Group gap={8}>
                <Text size="sm" fw={600}>Has Tailgate:</Text>
                {selectedTruck.hasTailgate === 'true' ? (
                  <Badge size="sm" style={{ backgroundColor: '#C6870018', color: '#C68700', border: '1px solid #C6870040' }}>
                    Yes — 15% FEMA capacity reduction applies
                  </Badge>
                ) : (
                  <Text size="sm" c="dimmed">No</Text>
                )}
              </Group>
            </div>

            {/* Driver info */}
            {(selectedTruck.driverName || selectedTruck.driverPhone) && (
              <div style={{ borderTop: '1px solid #e8eaf0', paddingTop: 12 }}>
                <Text size="sm" fw={600} mb={4}>Driver</Text>
                <Group gap={16}>
                  {selectedTruck.driverName && <Text size="sm">{selectedTruck.driverName}</Text>}
                  {selectedTruck.driverPhone && <Text size="sm" c="dimmed">{selectedTruck.driverPhone}</Text>}
                </Group>
              </div>
            )}

            {/* Notes */}
            {(selectedTruck.cleanNotes || '') && (
              <div style={{ borderTop: '1px solid #e8eaf0', paddingTop: 12 }}>
                <Text size="sm" fw={600} mb={4}>Notes</Text>
                <Text size="sm">{selectedTruck.cleanNotes || ''}</Text>
              </div>
            )}

            {/* Link to tickets */}
            <div style={{ borderTop: '1px solid #e8eaf0', paddingTop: 12 }}>
              <Button
                variant="light"
                size="sm"
                leftSection={<IconFileText size={16} />}
                onClick={() => {
                  setSelectedTruck(null);
                  navigate(`/debris/tickets?truck=${encodeURIComponent(selectedTruck.truckNumber)}`);
                }}
              >
                View all tickets for this truck
              </Button>
            </div>
          </Stack>
          );
        })()}
      </Modal>

      {/* ── Import Modal ── */}
      <Modal opened={showImport} onClose={() => { setShowImport(false); setImportFiles([]); setImportResults(null); }} title="Import Truck Certifications" size="lg">
        <Stack gap={12}>
          <Text size="sm" c="dimmed">
            Upload PDF certification files. Filenames should follow the pattern:
            <code style={{ display: 'block', padding: '8px 12px', background: '#f1f5f9', borderRadius: 6, marginTop: 4, fontSize: 13 }}>
              TampaAshbrittMilton_&#123;TruckNo&#125;_&#123;CapacityCY&#125;.pdf
            </code>
          </Text>

          <Select
            label="Contractor"
            data={contractors.map(c => ({ value: String(c.id), label: c.companyName }))}
            value={importContractor}
            onChange={(v: string | null) => setImportContractor(v || '')}
            required
            placeholder="Select contractor..."
          />

          <div>
            <Text size="sm" fw={600} mb={4}>Certification PDFs</Text>
            <div
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: '2px dashed #d0d5dd',
                borderRadius: 8,
                padding: '24px 16px',
                textAlign: 'center',
                cursor: 'pointer',
                background: '#fafbfc',
              }}
              onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#4B3FFF'; }}
              onDragLeave={e => { e.currentTarget.style.borderColor = '#d0d5dd'; }}
              onDrop={e => {
                e.preventDefault();
                e.currentTarget.style.borderColor = '#d0d5dd';
                const files = Array.from(e.dataTransfer.files).filter(f => f.type === 'application/pdf' || f.name.endsWith('.pdf'));
                setImportFiles(files);
              }}
            >
              <IconUpload size={28} color="#94a3b8" />
              <Text size="sm" c="dimmed" mt={4}>Click or drag PDFs here</Text>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
            </div>
            {importFiles.length > 0 && (
              <Text size="sm" mt={4} fw={600}>{importFiles.length} file(s) selected</Text>
            )}
          </div>

          {/* Preview parsed filenames */}
          {importFiles.length > 0 && (
            <div style={{ maxHeight: 200, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={{ padding: '6px 8px', textAlign: 'left', color: '#64748b', fontWeight: 600 }}>Filename</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', color: '#64748b', fontWeight: 600 }}>Truck #</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right', color: '#64748b', fontWeight: 600 }}>Capacity</th>
                    <th style={{ padding: '6px 8px', textAlign: 'center', color: '#64748b', fontWeight: 600 }}>Valid</th>
                  </tr>
                </thead>
                <tbody>
                  {importFiles.map((f, i) => {
                    const parsed = parseFilename(f.name);
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '6px 8px', fontSize: 12 }}>{f.name}</td>
                        <td style={{ padding: '6px 8px' }}>{parsed?.truckNumber || '—'}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right' }}>{parsed?.capacity ? `${parsed.capacity} CY` : '—'}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                          {parsed ? <IconCheck size={16} color="var(--color-success)" /> : <IconX size={16} color="var(--color-error)" />}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Import results */}
          {importResults && (
            <Paper withBorder p="sm" style={{ borderRadius: 6 }}>
              <Group gap={16}>
                <Text size="sm" fw={600} style={{ color: 'var(--color-success)' }}>{importResults.success} imported</Text>
                {importResults.failed > 0 && (
                  <Text size="sm" fw={600} style={{ color: 'var(--color-error)' }}>{importResults.failed} failed</Text>
                )}
              </Group>
              {importResults.errors.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  {importResults.errors.map((e, i) => (
                    <Text key={i} size="xs" c="dimmed">{e}</Text>
                  ))}
                </div>
              )}
            </Paper>
          )}

          <Group style={{ justifyContent: 'flex-end' }} gap={8}>
            <Button variant="light" onClick={() => { setShowImport(false); setImportFiles([]); setImportResults(null); }}>
              Cancel
            </Button>
            <Button
              onClick={runImport}
              loading={importing}
              disabled={importFiles.length === 0 || !importContractor}
              leftSection={<IconUpload size={16} />}
            >
              Import {importFiles.length} File(s)
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Inline PDF Viewer */}
      <Modal opened={!!pdfViewerUrl} onClose={() => { setPdfViewerUrl(null); setPdfViewerTitle(''); }}
        title={pdfViewerTitle} fullScreen
        styles={{ body: { padding: 0 }, header: { padding: '12px 20px' } }}>
        {pdfViewerUrl && (
          <iframe src={pdfViewerUrl} style={{ width: '100%', height: 'calc(100vh - 60px)', border: 'none', display: 'block' }} title={pdfViewerTitle} />
        )}
      </Modal>
    </Stack>
  );
}
